import { CSSProperties } from 'react';
import { IRetentionTable } from '../../interfaces';
import { assertCondition } from '../../../../utils/types';
import { theme } from '../../../../styles/theme';

/**
 * The utilities in this file add collapsing borders of adjacent cells of the
 * Retention table. None of the CSS-only solutions worked for non-table-like
 * cells (outline, border and negative margin to compensate)
 *
 * The idea is to always add top / left border to the cells of the first column
 * and row, and then we omit them for the adjacent cells on the right and bottom.
 *
 * The staircase-like Retention table, the ability sort the table
 * (inverting the staircase), the 1 or 2 fixed columns and the fact that we
 * don't have the actual visual row and column index makes it even more difficult.
 *
 * The helpers topRow, middleRow, middleStaircaseRow compute when the top / left
 * border should be displayed
 *
 */

export const BORDER_STYLE = `1px solid ${theme.colors.border}`;

interface BorderPresence {
  top: boolean;
  left: boolean;
}

export const topRowCell = (visualColumnIndex: number): BorderPresence => ({
  top: true, // top rows always have top border - this is the first header
  left: visualColumnIndex === 0, // collapse with the right border of the cell on the left
});

// any middle row that is not staircase don't have top border (it reuses the
// first or previous row's bottom border. Left border is available only for the
// first *visual column*. These are: secondary header row or the
// first or second fixed columns (they are never staircase)
export const middleRowCell = (visualColumnIndex: number): BorderPresence => ({
  top: false, // collapse with the bottom border of the cell on the top
  left: visualColumnIndex === 0, // collapse with the right border of the cell on the left
});

// Retention cells needs special consideration because of the sorting
// We display top border only if there is *no* cell above it.
// If we are first row we don't render border because we reuse header's bottom
// border (it is always present)
// We never render left border because we reuse the right border of the
// adjacent column on the left (it is always present)
export const staircaseReturnCell = (
  table: IRetentionTable,
  tableRowIndex: number,
  tableReturnIndex: number,
): BorderPresence => {
  assertCondition(tableRowIndex >= 0 && tableRowIndex < table.rows.length);
  return {
    top:
      tableRowIndex === 0
        ? // collapse with the bottom border of the cell on the top
          false
        : // only if there is no return cell on the top, otherwise - collapse
          tableReturnIndex >= table.rows[tableRowIndex - 1].returns.length,
    left: false, // collapse with the right border of the cell on the left
  };
};

export const styleWithBorder = (
  multiGridStyles: CSSProperties,
  { left, top }: BorderPresence,
) => {
  const styles = { ...multiGridStyles };
  styles.borderBottom = BORDER_STYLE;
  styles.borderRight = BORDER_STYLE;
  if (top) {
    styles.borderTop = BORDER_STYLE;
  }
  if (left) {
    styles.borderLeft = BORDER_STYLE;
  }

  return styles;
};
